<template>
  <div :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div v-if="showMethodSelector === true" class="grid grid-cols-1 lg:grid-cols-3 mt-4">
      <button
        v-for="method in fineTuneMethods"
        :key="method.id"
        type="button"
        class="text-center border rounded-box p-4 mb-3 lg:mb-0 lg:mr-3 transition-colors"
        :class="activeSelectedMethod === method.id ? 'border-primary bg-primary/5 shadow-sm' : 'border-base-300 bg-base-100 hover:bg-base-200'"
        @click="activeSelectedMethod = method.id"
      >
        <div class="flex flex-col items-center">
          <Icon :icon="method.icon" class="w-7 h-7 text-primary shrink-0 mb-2" />
          <div>
            <h2 class="font-semibold">{{ method.title }}</h2>
            <p class="text-xs text-gray-500 mt-1">{{ method.description }}</p>
          </div>
        </div>
      </button>
    </div>

    <section v-if="showContent === true && activeSelectedMethod === 'answers'" class="mt-4">
      <div class="alert alert-info text-sm mb-4">
        <div>
          <div><strong>{{ t('aiChatAgentSetup.fineTuneQA.pros', 'Pros:') }}</strong> {{ t('aiChatAgentSetup.fineTuneQA.answerOverridePros', 'Easy control over each answer and similar questions.') }}</div>
          <div class="mt-1"><strong>{{ t('aiChatAgentSetup.fineTuneQA.cons', 'Cons:') }}</strong> {{ t('aiChatAgentSetup.fineTuneQA.answerOverrideCons', 'Overrides uploaded knowledge-base information. Later knowledge-base updates remain overridden until the override is manually removed from the second tab in this setup step.') }}</div>
        </div>
      </div>
      <div v-if="reportsForbidden === false" class="bg-base-100 border-base-300 rounded-box p-1">
        <div class="flex justify-between mb-4">
          <div class="form-control" style="width:16rem;">
            <label class="label">
              <span class="label-text">{{ t('reports.dateRangeLabel', 'Date Range') }}</span>
            </label>
            <CustomDateRangePicker
              v-model="dateRange"
              :clearable="false"
              :multi-calendars="true"
              :placeholder="t('reports.selectDateRangePlaceholder', 'Select Date Range')"
            />
          </div>
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ t('reports.quickSelectLabel', 'Quick Select') }}</span>
            </label>
            <select
              v-model="selectedPreset"
              class="select select-bordered"
              @change="applyPreset"
            >
              <option value="">{{ t('reports.customRangeOption', 'Custom Range') }}</option>
              <option v-for="(label, key) in presets" :key="key" :value="key">
                {{ t('reports.presets.' + key, label) }}
              </option>
            </select>
          </div>
          <div class="form-control" style="display: flex; justify-content: flex-end; align-items: flex-end;">
            <button
              class="btn w-full"
              @click="generateReport"
              :disabled="isFormValid === false"
            >
              {{ t('reports.refreshReportButton', 'Refresh Report') }}
            </button>
          </div>
        </div>
        <div class="flex items-center mb-4 fine-tune-actions-row" :class="isRtl === true ? 'fine-tune-actions-row--rtl' : 'fine-tune-actions-row--ltr'">
            <button @click="exportToXLSX" class="btn btn-info">
              <Icon icon="heroicons:arrow-down-tray" class="h-5 w-5 fine-tune-action-icon" />
              {{ t('reports.exportToXLSX', 'Export to XLSX') }}
            </button>
            <label class="btn btn-info fine-tune-import-label">
              <Icon icon="heroicons:arrow-up-tray" class="h-5 w-5 fine-tune-action-icon" />
              {{ t('reports.importFromXLSX', 'Import from XLSX') }}
              <input type="file" @change="handleImport" class="hidden" accept=".xlsx" />
            </label>
            <button @click="openBulkAnswerModal" class="btn btn-secondary h-auto min-h-0 py-2" :disabled="itemsSelected.length === 0">
              <Icon icon="heroicons:pencil-square" class="h-5 w-5 fine-tune-action-icon" />
              {{ t('reports.fineTuneSelected', 'Fine Tune Selected') }} ({{ itemsSelected.length }})
            </button>
            <button @click="showManualAddModal = true" class="btn hidden">
              <Icon icon="heroicons:plus" class="h-5 w-5 fine-tune-action-icon" />
              {{ t('reports.addNewQA', 'Add New Q&A') }}
            </button>
        </div>
        <div
          class="overflow-x-auto text-align mt-6 card shadow bg-white dark:bg-gray-800 w-full rounded-md p-3 border dark:border-gray-700 flex fine-tune-table-shell"
          :class="isRtl === true ? 'fine-tune-table-shell--rtl' : 'fine-tune-table-shell--ltr'"
          style="border-radius: 10px; width:98%; height:70vh;"
        >
          <div class="flex mb-6 fine-tune-filters-row" style="align-items: flex-end;">
            <div class="form-control flex-grow" style="width: 60%;">
              <div class="input-group" >
                <input 
                  type="text" 
                  v-model="searchValue" 
                  :placeholder="t('reports.searchPlaceholder', 'Search in report...')" 
                  class="input input-bordered w-full"
                />
              </div>
            </div>
            <div class="form-control" style="width: 20%;">
              <label class="label">
                <span class="label-text">{{ t('reports.filterByFeedback', 'Filter by Feedback') }}</span>
              </label>
              <select v-model="selectedFeedbackFilter" class="select select-bordered w-full max-w-xs">
                <option value="all">{{ t('reports.allFeedback', 'All Feedback') }}</option>
                <option value="like">{{ t('reports.liked', 'Liked') }}</option>
                <option value="dislike">{{ t('reports.disliked', 'Disliked') }}</option>
              </select>
            </div>
          </div>
          <ThemeOnly theme="modern" :invert="true">
            <EasyDataTable
              v-model:items-selected="itemsSelected"
            :headers="reportTableHeaders"
            :items="filteredReportData"
            buttons-pagination
            :search-value="searchValue"
            :search-field="searchFields"
            :search-options="{ enabled: true, debounceTime: 300 }"
            :rows-items="[10, 25, 50, 100]"
            :rows-per-page="25"
            :rows-per-page-message="t('reports.rowsPerPageMessage', 'Rows per page:')"
          >
            <template #item-totalconversations="{ totalconversations }">
              {{ totalconversations }}
            </template>
            <template #item-uniquesessions="{ uniquesessions }">
              {{ uniquesessions }}
            </template>
            <template #item-uniquequeries="{ uniquequeries }">
              {{ uniquequeries }}
            </template>
            <template #item-avgresponselength="{ avgresponselength }">
              {{ Math.round(avgresponselength) }} {{ t('reports.charsSuffix', 'chars') }}
            </template>
            <template #item-userquery="{ userquery }">
              <div class="user-query-cell">{{ userquery }}</div>
            </template>
            <template #item-response="{ response }">
              <div class="ai-response" :class="isRtl === true ? 'ai-response--rtl' : 'ai-response--ltr'" v-html="sanitizeHtml(response)"></div>
            </template>
            <template #item-createdat="{ createdat }">
              <div class="timestamp-cell">{{ formatDisplayDate(createdat) }}</div>
            </template>
            <template #item-feedbacktype="{ feedbacktype }">
              <div v-if="(feedbacktype !== undefined) && (feedbacktype !== null) && (feedbacktype !== '')" 
                   class="rounded-full border w-10 h-10 m-4 flex items-center justify-center"
                   :class="{
                     'border-success text-success': feedbacktype === 'like',
                     'border-error text-error': feedbacktype === 'dislike'
                   }">
                <span v-if="feedbacktype === 'like'" :title="t('reports.liked', 'Liked')" class="text-lg">👍</span>
                <span v-else-if="feedbacktype === 'dislike'" :title="t('reports.disliked', 'Disliked')" class="text-lg">👎</span>
              </div>
              <span v-else class="text-gray-400">-</span>
            </template>
            <template #item-sessionid="{ sessionid }">
              <div class="session-id-cell" :title="sessionid">{{ formatSessionId(sessionid) }}</div>
            </template>
           
            <template #item-options="item">
              <div class="flex items-center fine-tune-row-actions" :class="isRtl === true ? 'fine-tune-row-actions--rtl' : 'fine-tune-row-actions--ltr'">
                <button
                  class="btn btn-sm fine-tune-row-action-btn"
                  @click.stop="openSourcesModal(item.sessionid, item.messageid)"
                >
                  <Icon icon="tabler:book" class="fine-tune-row-action-icon" />
                  {{ t('reports.sourcesModal.button') }}
                </button>
                <button
                  class="btn btn-sm fine-tune-row-action-btn"
                  @click.stop="openReasoningModal(item)"
                >
                  <Icon icon="tabler:brain" class="fine-tune-row-action-icon" />
                  {{ t('reports.reasoningModal.button') }}
                </button>
                <button
                  class="btn btn-sm h-auto min-h-0 py-1 px-2"
                  @click="openAnswerModal(item)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" fill-rule="evenodd"><path d="m12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036q-.016-.004-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092q.019.005.029-.008l.004-.014l-.034-.614q-.005-.019-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z"/><path fill="#131313" d="M16 15c1.306 0 2.418.835 2.83 2H20a1 1 0 1 1 0 2h-1.17a3.001 3.001 0 0 1-5.66 0H4a1 1 0 1 1 0-2h9.17A3 3 0 0 1 16 15m0 2a1 1 0 1 0 0 2a1 1 0 0 0 0-2M8 9a3 3 0 0 1 2.762 1.828l.067.172H20a1 1 0 0 1 .117 1.993L20 13h-9.17a3.001 3.001 0 0 1-5.592.172L5.17 13H4a1 1 0 0 1-.117-1.993L4 11h1.17A3 3 0 0 1 8 9m0 2a1 1 0 1 0 0 2a1 1 0 0 0 0-2m8-8c1.306 0 2.418.835 2.83 2H20a1 1 0 1 1 0 2h-1.17a3.001 3.001 0 0 1-5.66 0H4a1 1 0 0 1 0-2h9.17A3 3 0 0 1 16 3m0 2a1 1 0 1 0 0 2a1 1 0 0 0 0-2"/></g></svg>
                  {{ t('aiChatAgentSetup.fineTuneQA.actions.fineTuneAnswer', 'Fine Tune Answer') }}
                </button>
              </div>
            </template>
            <template #loading>
              <ModernLoader />
            </template>
          </EasyDataTable>
          </ThemeOnly>
        </div>
      </div>
      <div v-else class="bg-base-100 border-base-300 rounded-box p-6 mt-4">
        <div class="alert alert-warning">
          <Icon icon="tabler:lock" class="h-5 w-5" />
          <span>{{ reportsForbiddenMessage }}</span>
        </div>
      </div>
    </section>

    <section v-if="showContent === true && activeSelectedMethod === 'questions'" class="mt-4 border border-base-300 rounded-box bg-base-100 p-5">
      <h2 class="text-lg font-semibold">{{ t('aiChatAgentSetup.fineTuneQA.learnQaTitle', 'Learn by questions and answers') }}</h2>
      <p class="text-sm text-gray-500 mt-1 mb-4">{{ t('aiChatAgentSetup.fineTuneQA.learnQaDescription', 'Teach questions, answers, hidden context, and company know-how as knowledge. This guides the AI without creating exact answer overrides.') }}</p>
      <div class="flex flex-wrap items-center">
        <button type="button" class="btn mr-3 mb-3" @click="downloadQuestionAnswerTemplate">
          <Icon icon="heroicons:arrow-down-tray" class="w-5 h-5 mr-2" />
          {{ t('aiChatAgentSetup.fineTuneQA.downloadQaTemplate', 'Download Excel template') }}
        </button>
        <label class="btn mb-3">
          <Icon icon="heroicons:arrow-up-tray" class="w-5 h-5 mr-2" />
          {{ t('aiChatAgentSetup.fineTuneQA.uploadQaExcel', 'Upload Excel') }}
          <input type="file" class="hidden" accept=".xlsx,.xls" @change="handleQuestionAnswerImport" />
        </label>
      </div>
      <div v-if="questionAnswerImportSummary.length > 0" class="alert alert-success text-sm mt-3">{{ questionAnswerImportSummary }}</div>
      <div class="mt-5 pt-5 border-t border-base-300">
        <ExcelFineTuningPanel
          :agent-id="props.agentID"
          :setup-type="resolvedSetupType"
          :questions-mode="true"
          @learn-question-answers="relayLearnedQuestionAnswers"
          @approve-generated-rules="relayGeneratedRules"
        />
      </div>
    </section>

    <section v-if="showContent === true && activeSelectedMethod === 'testedResponses'" class="mt-4 border border-base-300 rounded-box bg-base-100 p-5">
      <h2 class="text-lg font-semibold">{{ t('aiChatAgentSetup.fineTuneQA.testedResponsesTitle', 'Fine tune from tested responses') }}</h2>
      <p class="text-sm text-gray-500 mt-1 mb-4">{{ t('aiChatAgentSetup.fineTuneQA.testedResponsesDescription', 'Use Excel with AI answers, wrong answers, and correct answers to learn context, fixes, and organizational know-how.') }}</p>
      <ExcelFineTuningPanel :agent-id="props.agentID" :setup-type="resolvedSetupType" :tested-responses-mode="true" @approve-generated-rules="relayGeneratedRules" />
    </section>
  </div>
    <div v-if="showAddAnswerModal === true" class="custom-modal">
      <div class="custom-modal-box custom-modal-box-xl">
        <div class="custom-modal-header flex items-center justify-between">
          <h3 class="font-bold text-lg">
            {{ t('aiChatAgentSetup.fineTuneQA.addAnswerTitle', 'How would you like to fine tune the answer?') }}
          </h3>
          <button class="btn btn-sm btn-circle btn-ghost" @click="closeAddAnswer()">✕</button>
        </div>
        <div class="custom-modal-body py-4">
          <label class="label">
            <span class="label-text">{{ t('aiChatAgentSetup.fineTuneQA.originalAnswerLabel', 'Original Answer') }}</span>
          </label>
          <div class="original-answer-card" v-html="sanitizeHtml(currentOriginalAnswer)"></div>
          <label class="label mt-4">
            <span class="label-text">{{ t('aiChatAgentSetup.fineTuneQA.answerLabel', 'Answer') }}</span>
          </label>
          <textarea ref="newAnswerTextarea" @input="autoResizeNewAnswer"
            v-model="newAnswer"
            class="textarea textarea-bordered w-full resize-y new-answer-textarea auto-grow-textarea"
            :placeholder="t('aiChatAgentSetup.fineTuneQA.addAnswerPlaceholder', 'Enter your answer here')"
          ></textarea>
          <div class="flex justify-end text-xs opacity-70 mt-2">{{ t('aiChatAgentSetup.fineTuneQA.characters', 'Characters') }}: {{ newAnswer.length }}</div>
        </div>
        <div class="custom-modal-footer">
          <button class="btn btn-ghost" @click="closeAddAnswer()">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="saveAnswer">{{ t('aiChatAgentSetup.buttons.save', 'Save') }}</button>
        </div>
      </div>
    </div>
    <dialog v-if="showManualAddModal === true" open class="modal">
      <div class="modal-box modal-box-md">
        <div class="modal-header flex items-center justify-between">
          <h3 class="font-bold text-lg">
            {{ t('aiChatAgentSetup.fineTuneQA.addManualQATitle', 'Add New Question & Answer') }}
          </h3>
          <button class="btn btn-sm btn-circle btn-ghost" @click="closeManualAdd()">✕</button>
        </div>
        <div class="modal-body py-4 space-y-4">
          <div>
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.fineTuneQA.questionLabel', 'Question') }}</span>
            </label>
            <textarea
              v-model="manualQuestion"
              class="textarea textarea-bordered w-full resize-y auto-grow-textarea"
              @input="autoGrowIfNeeded($event.target)"
              :placeholder="t('aiChatAgentSetup.fineTuneQA.questionPlaceholder', 'Enter the question')"
            ></textarea>
            <div class="flex justify-end text-xs opacity-70 mt-1">{{ t('aiChatAgentSetup.fineTuneQA.characters', 'Characters') }}: {{ manualQuestion.length }}</div>
          </div>
          <div>
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.fineTuneQA.answerLabel', 'Answer') }}</span>
            </label>
            <textarea
              v-model="manualAnswer"
              class="textarea textarea-bordered w-full resize-y auto-grow-textarea"
              @input="autoGrowIfNeeded($event.target)"
              :placeholder="t('aiChatAgentSetup.fineTuneQA.answerPlaceholder', 'Enter the desired answer')"
            ></textarea>
            <div class="flex justify-end text-xs opacity-70 mt-1">{{ t('aiChatAgentSetup.fineTuneQA.characters', 'Characters') }}: {{ manualAnswer.length }}</div>
          </div>
        </div>
        <div class="modal-footer modal-action">
          <button class="btn btn-ghost" @click="closeManualAdd()">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="saveManualQA">{{ t('aiChatAgentSetup.buttons.save', 'Save') }}</button>
        </div>
      </div>
    </dialog>
    <dialog v-if="showBulkAddAnswerModal === true" open class="modal">
      <div class="modal-box modal-box-lg w-11/12 max-w-5xl">
        <div class="modal-header flex items-center justify-between">
          <h3 class="font-bold text-lg">
            {{ t('aiChatAgentSetup.fineTuneQA.bulkAddAnswerTitle', 'Fine Tune Selected Questions') }}
          </h3>
          <button class="btn btn-sm btn-circle btn-ghost" @click="closeBulkAdd()">✕</button>
        </div>
        <div class="modal-body py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          <div v-for="(item, index) in itemsSelected" :key="item.userquery" class="p-4 border rounded-md">
            <label class="label">
              <span class="label-text font-semibold">{{ t('aiChatAgentSetup.fineTuneQA.questionLabel', 'Question') }}:</span>
            </label>
            <p class="bg-base-200 p-2 rounded-md mb-2">{{ item.userquery }}</p>
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.fineTuneQA.originalAnswerLabel', 'Original Answer') }}</span>
            </label>
            <div class="original-answer-card" v-html="sanitizeHtml(item.response)"></div>
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.fineTuneQA.answerLabel', 'Answer') }}</span>
            </label>
            <textarea
              ref="bulkAnswerRefs"
              v-model="bulkAnswers[index]"
              class="textarea textarea-bordered w-full resize-y auto-grow-textarea"
              @input="autoGrowIfNeeded($event.target)"
              :placeholder="t('aiChatAgentSetup.fineTuneQA.addAnswerPlaceholder', 'Enter your answer here')"
            ></textarea>
            <div class="flex justify-end text-xs opacity-70 mt-1">{{ t('aiChatAgentSetup.fineTuneQA.characters', 'Characters') }}: {{ (bulkAnswers[index] || '').length }}</div>
          </div>
        </div>
        <div class="modal-footer modal-action">
          <button class="btn" @click="closeBulkAdd()">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="saveBulkAnswers">{{ t('aiChatAgentSetup.buttons.saveAll', 'Save All') }}</button>
        </div>
      </div>
    </dialog>
    <ReasoningModal
      :visible="isReasoningModalOpen === true"
      :session-id="reasoningSessionId"
      :message-id="reasoningMessageId"
      @close="closeReasoningModal"
    />
    <SourcesModal
      :visible="isSourcesModalOpen === true"
      :session-id="sourcesSessionId"
      :message-id="sourcesMessageId"
      @close="closeSourcesModal"
    />

</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import axios from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import CustomDateRangePicker from '@/components/CustomDateRangePicker.vue';
import router from '@/router';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';
import * as XLSX from 'xlsx';
import { Icon } from "@iconify/vue/dist/offline";
import ModernLoader from '@/components/ModernLoader.vue';
import ReasoningModal from '@/components/ReasoningModal.vue';
import SourcesModal from '@/components/SourcesModal.vue';
import ThemeOnly from '@/components/ThemeOnly.vue';
import ExcelFineTuningPanel from '@/components/ExcelFineTuningPanel.vue';

const { t, currentLanguage } = useI18n();
const route = useRoute();

const isRtl = computed(() => currentLanguage.value === 'he' || currentLanguage.value === 'ar');

const props = defineProps({
  agentID: { type: String, default: '' },
  selectedMethod: { type: String, default: '' },
  showMethodSelector: { type: Boolean, default: true },
  showContent: { type: Boolean, default: true },
  setupType: { type: String, default: '' }
});
const toast = useToast();
const resolvedSetupType = computed(() => {
  if (props.setupType === 'light') {
    return 'light';
  }
  const routeSetupType = Array.isArray(route.query.setupType) === true ? route.query.setupType[0] : route.query.setupType;
  if (routeSetupType === 'light' || route.path === '/aiChatAgentSetupLight') {
    return 'light';
  }
  return 'full';
});

const emit = defineEmits(['save-fine-tune', 'learn-question-answers', 'approve-generated-rules', 'update:selectedMethod']);

const selectedAgent = ref('');
const internalSelectedMethod = ref('answers');
const questionAnswerImportSummary = ref('');
const activeSelectedMethod = computed({
  get() {
    return props.selectedMethod !== '' ? props.selectedMethod : internalSelectedMethod.value;
  },
  set(value) {
    internalSelectedMethod.value = value;
    emit('update:selectedMethod', value);
  }
});
const fineTuneMethods = computed(() => [
  {
    id: 'answers',
    icon: 'tabler:message-check',
    title: t('aiChatAgentSetup.fineTuneQA.answerOverrideTitle', 'Fine tune answers'),
    description: t('aiChatAgentSetup.fineTuneQA.answerOverrideDescription', 'Override AI answers generated from knowledge-base information.')
  },
  {
    id: 'questions',
    icon: 'tabler:school',
    title: t('aiChatAgentSetup.fineTuneQA.learnQaTitle', 'Learn by questions and answers'),
    description: t('aiChatAgentSetup.fineTuneQA.learnQaCardDescription', 'Learn Q&A, hidden context, and company know-how without exact overrides.')
  },
  {
    id: 'testedResponses',
    icon: 'tabler:table-check',
    title: t('aiChatAgentSetup.fineTuneQA.testedResponsesTitle', 'Fine tune from tested responses'),
    description: t('aiChatAgentSetup.fineTuneQA.testedResponsesCardDescription', 'Use reviewed Excel responses to learn fixes and organizational context.')
  }
]);

const selectedReport = ref('fineTuneReport');
const dateRange = ref([]);
const selectedPreset = ref('');
const searchValue = ref('');
const reportData = ref([]);
const searchFields = ref([]);
const aiAgents = ref([]);
const selectedFeedbackFilter = ref('all');

const isInitialReportLoad = ref(true);
const reportsForbidden = ref(false);
const reportsForbiddenMessage = ref('');

function sanitizeHtml(rawHtml) {
  const input = typeof rawHtml === 'string' ? rawHtml : (rawHtml === null || rawHtml === undefined ? '' : String(rawHtml));
  if (input.trim().length === 0) {
    return '';
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString('<div>' + input + '</div>', 'text/html');
  const container = doc.body.firstChild;
  if (container === undefined || container === null) {
    return '';
  }
  const allowed = new Set(['div', 'span', 'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td']);
  function clean(node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      if (allowed.has(tag) === false) {
        const parent = node.parentNode;
        while (node.firstChild !== null) {
          parent.insertBefore(node.firstChild, node);
        }
        parent.removeChild(node);
        return;
      }
      if (tag === 'a') {
        const href = node.getAttribute('href') !== null ? node.getAttribute('href') : '';
        if (/^(https?:|mailto:|tel:|#)/i.test(href) === false) {
          node.removeAttribute('href');
        } else {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      }
      if (node.hasAttribute('style') === true) {
        const style = node.getAttribute('style') !== null ? node.getAttribute('style') : '';
        const parts = style.split(';').map(function(p){ return p.trim(); }).filter(function(p){ return p.length > 0; });
        const safeParts = parts.filter(function(p){ const l = p.toLowerCase(); return l.indexOf('background-color') === 0 || l.indexOf('color') === 0; });
        if (safeParts.length > 0) {
          node.setAttribute('style', safeParts.join(';'));
        } else {
          node.removeAttribute('style');
        }
      }
      const attributes = Array.from(node.attributes);
      for (let attributeIndex = 0; attributeIndex < attributes.length; attributeIndex = attributeIndex + 1) {
        const attr = attributes[attributeIndex];
        if (/^on/i.test(attr.name) === true) {
          node.removeAttribute(attr.name);
        }
      }
    }
    let child = node.firstChild;
    while (child !== null) {
      const next = child.nextSibling;
      clean(child);
      child = next;
    }
  }
  clean(container);
  return container.innerHTML.trim();
}

const showManualAddModal = ref(false);
const manualQuestion = ref('');
const manualAnswer = ref('');

const presets = {
  today: 'Today',
  thisWeek: 'This Week',
  lastWeek: 'Last Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month'
};
const presetRanges = {
  today: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    return [today, endOfDay];
  },
  thisWeek: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    return [startOfWeek, endOfDay];
  },
  lastWeek: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfLastWeek = new Date(today);
    startOfLastWeek.setDate(today.getDate() - today.getDay() - 7);
    const endOfLastWeek = new Date(today);
    endOfLastWeek.setDate(today.getDate() - today.getDay() - 1);
    endOfLastWeek.setHours(23, 59, 59, 999);
    return [startOfLastWeek, endOfLastWeek];
  },
  thisMonth: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    return [startOfMonth, endOfDay];
  },
  lastMonth: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    endOfLastMonth.setHours(23, 59, 59, 999);
    return [startOfLastMonth, endOfLastMonth];
  }
};

const isFormValid = computed(() => {
  return dateRange.value !== null && dateRange.value.length === 2 && dateRange.value[0] <= dateRange.value[1];
});
const applyPreset = () => {
  if ((selectedPreset.value !== '') && (presetRanges[selectedPreset.value] !== undefined)) {
    dateRange.value = presetRanges[selectedPreset.value]();
  }
};
const getAuthToken = () => {
  const token = document.cookie.split('; ').find(row => row.startsWith('authToken=') === true)?.split('=')[1];
  if (token === undefined || token === null) {
    return '';
  }
  return token;
};
const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};
const handleAuthError = (error) => {
  if (error.response?.status === 401) {
    router.push('/login');
  }
};
searchFields.value = ['sessionid', 'userquery', 'response', 'createdat', 'feedbacktype'];

const reportTableHeaders = computed(() => {
  return [
    { text: t('reports.headers.sessionID', 'Session ID'), value: 'sessionid', sortable: true, width: 88 },
    { text: t('reports.headers.userMessage', 'User Query'), value: 'userquery', sortable: true, width: 200 },
    { text: t('reports.headers.agentResponse', 'AI Response'), value: 'response', sortable: true },
    { text: t('reports.headers.createdAt', 'Timestamp'), value: 'createdat', sortable: true },
    { text: t('reports.headers.feedback', 'Feedback'), value: 'feedbacktype', sortable: true },
    { text: t('reports.headers.options', 'Options'), value: 'options', sortable: false }
  ];
});

function getExportColumnLabels() {
  return {
    userQuery: t('reports.fineTune.exportColumns.userQuery', 'User Query'),
    aiResponse: t('reports.fineTune.exportColumns.aiResponse', 'AI Response'),
    timestamp: t('reports.fineTune.exportColumns.timestamp', 'Timestamp'),
    feedback: t('reports.fineTune.exportColumns.feedback', 'Feedback'),
    fineTunedAnswer: t('reports.fineTune.exportColumns.fineTunedAnswer', 'Fine-Tuned Answer')
  };
}

function readImportCell(row, localizedLabel, englishLabel) {
  if (row[localizedLabel] !== undefined && row[localizedLabel] !== null) {
    return row[localizedLabel];
  }
  if (row[englishLabel] !== undefined && row[englishLabel] !== null) {
    return row[englishLabel];
  }
  return undefined;
}

function normalizeImportHeader(value) {
  return String(value).trim().toLowerCase().replace(/[\s_\-–—]+/g, '');
}

function readQuestionAnswerCell(row, aliases) {
  const keys = Object.keys(row);
  for (let keyIndex = 0; keyIndex < keys.length; keyIndex = keyIndex + 1) {
    const normalizedKey = normalizeImportHeader(keys[keyIndex]);
    for (let aliasIndex = 0; aliasIndex < aliases.length; aliasIndex = aliasIndex + 1) {
      if (normalizedKey === normalizeImportHeader(aliases[aliasIndex])) {
        const value = row[keys[keyIndex]];
        return value !== undefined && value !== null ? String(value).trim() : '';
      }
    }
  }
  return '';
}

function getTestedResponseCellText(value) {
  if (value === undefined) {
    return '';
  }
  if (value === null) {
    return '';
  }
  return String(value).trim();
}

function findTestedResponseHeaderRow(rows) {
  const maxIndex = rows.length > 10 ? 10 : rows.length;
  for (let rowIndex = 0; rowIndex < maxIndex; rowIndex = rowIndex + 1) {
    const row = Array.isArray(rows[rowIndex]) === true ? rows[rowIndex] : [];
    let populatedCells = 0;
    for (let cellIndex = 0; cellIndex < row.length; cellIndex = cellIndex + 1) {
      if (getTestedResponseCellText(row[cellIndex]).length > 0) {
        populatedCells = populatedCells + 1;
      }
    }
    if (populatedCells >= 2) {
      return rowIndex;
    }
  }
  return -1;
}

function findTestedResponseColumn(headers, aliases) {
  for (let headerIndex = 0; headerIndex < headers.length; headerIndex = headerIndex + 1) {
    const header = normalizeImportHeader(headers[headerIndex]);
    for (let aliasIndex = 0; aliasIndex < aliases.length; aliasIndex = aliasIndex + 1) {
      if (header === normalizeImportHeader(aliases[aliasIndex])) {
        return headerIndex;
      }
    }
  }
  return -1;
}

function isTestedResponseWrongStatus(value) {
  const status = getTestedResponseCellText(value).toLowerCase();
  const markedStatuses = ['no', 'partial', 'partially', 'yes and no', 'yes/no', 'לא', 'חלקית', 'חלקי', 'כן ולא', 'כן/לא'];
  return markedStatuses.includes(status);
}

function getTestedResponseQuestionAnswerEntries(rows) {
  const headerRowIndex = findTestedResponseHeaderRow(rows);
  if (headerRowIndex < 0) {
    return null;
  }
  const headers = Array.isArray(rows[headerRowIndex]) === true ? rows[headerRowIndex] : [];
  const questionColumn = findTestedResponseColumn(headers, ['Question', 'User Question', 'Query', 'שאלה', 'שאלת משתמש', 'שאלת המשתמש', 'הודעת הפונה לAI', 'السؤال']);
  const correctAnswerColumn = findTestedResponseColumn(headers, ['Correct Full Answer', 'Correct Answer', 'Expected Answer', 'אם התשובה לא הייתה תקינה מה התשובה המלאה התקינה צריכה להיות', 'אם התשובה לא היתה תקינה מה התשובה המלאה התקינה צריכה להיות', 'מה התשובה המלאה התקינה צריכה להיות', 'התשובה המלאה התקינה', 'תשובה מלאה תקינה']);
  const statusColumn = findTestedResponseColumn(headers, ['Response OK', 'Answer OK', 'Status', 'האם היא הייתה תקינה או לא', 'האם היא היתה תקינה או לא', 'האם התשובה תקינה או לא', 'האם התשובה הייתה תקינה או לא', 'האם התשובה היתה תקינה או לא', 'תקין', 'תקינות', 'סטטוס']);
  if (questionColumn < 0) {
    return null;
  }
  if (correctAnswerColumn < 0) {
    return null;
  }
  if (statusColumn < 0) {
    return null;
  }
  const entries = [];
  for (let rowIndex = headerRowIndex + 1; rowIndex < rows.length; rowIndex = rowIndex + 1) {
    const row = Array.isArray(rows[rowIndex]) === true ? rows[rowIndex] : [];
    if (isTestedResponseWrongStatus(row[statusColumn]) !== true) {
      continue;
    }
    const question = getTestedResponseCellText(row[questionColumn]);
    const answer = getTestedResponseCellText(row[correctAnswerColumn]);
    if (question.length > 0 && answer.length > 0) {
      entries.push({
        originalQuery: question,
        originalResponse: '',
        fineTunedAnswer: answer,
        hiddenContext: '',
        companyKnowHow: '',
        forceExactAnswer: false,
        suppressToast: true
      });
    }
  }
  return entries;
}

function downloadQuestionAnswerTemplate() {
  const data = [{
    Question: '',
    Answer: '',
    'Hidden Context': '',
    'Company Know-How': ''
  }];
  const worksheet = XLSX.utils.json_to_sheet(data);
  worksheet['!cols'] = [{ wch: 35 }, { wch: 55 }, { wch: 45 }, { wch: 45 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions and Answers');
  XLSX.writeFile(workbook, 'QuestionAnswerKnowledgeTemplate.xlsx');
}

function handleQuestionAnswerImport(event) {
  questionAnswerImportSummary.value = '';
  const target = event.target;
  if (target === undefined || target === null || target.files === undefined || target.files === null || target.files.length === 0) {
    return;
  }
  const reader = new FileReader();
  reader.onload = (loadEvent) => {
    const result = loadEvent !== undefined && loadEvent !== null && loadEvent.target !== undefined && loadEvent.target !== null ? loadEvent.target.result : null;
    if (result === null) {
      toast.error(t('aiChatAgentSetup.fineTuneQA.qaImportFailed', 'Could not read the Excel file.'));
      return;
    }
    try {
      const workbook = XLSX.read(new Uint8Array(result), { type: 'array' });
      const firstSheet = workbook.SheetNames[0];
      const worksheet = firstSheet !== undefined ? workbook.Sheets[firstSheet] : undefined;
      const matrixRows = worksheet !== undefined ? XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) : [];
      const testedResponseEntries = getTestedResponseQuestionAnswerEntries(matrixRows);
      const entries = testedResponseEntries === null ? [] : testedResponseEntries;
      if (testedResponseEntries === null) {
        const rows = worksheet !== undefined ? XLSX.utils.sheet_to_json(worksheet, { defval: '' }) : [];
        for (let rowIndex = 0; rowIndex < rows.length; rowIndex = rowIndex + 1) {
          const row = rows[rowIndex];
          const question = readQuestionAnswerCell(row, ['Question', 'User Question', 'Query', 'שאלה', 'שאלת משתמש', 'הודעת הפונה לAI', 'السؤال']);
          const answer = readQuestionAnswerCell(row, ['Answer', 'Correct Answer', 'Response', 'תשובה', 'תשובה נכונה', 'תשובת הAI', 'الإجابة']);
          const hiddenContext = readQuestionAnswerCell(row, ['Hidden Context', 'Context', 'Internal Context', 'הקשר נסתר', 'הקשר פנימי', 'السياق المخفي']);
          const companyKnowHow = readQuestionAnswerCell(row, ['Company Know-How', 'Company Know How', 'Organizational Know-How', 'Know-How', 'ידע ארגוני', 'ידע חברה', 'معرفة الشركة']);
          if (question.length > 0 && answer.length > 0) {
            entries.push({
              originalQuery: question,
              originalResponse: '',
              fineTunedAnswer: answer,
              hiddenContext,
              companyKnowHow,
              forceExactAnswer: false,
              suppressToast: true
            });
          }
        }
      }
      if (entries.length === 0) {
        toast.warning(t('aiChatAgentSetup.fineTuneQA.qaImportNoRows', 'No valid question and answer rows were found.'));
        return;
      }
      emit('learn-question-answers', entries);
      questionAnswerImportSummary.value = t('aiChatAgentSetup.fineTuneQA.qaImportCount', '{count} knowledge entries are ready. Save the agent to add them to the knowledge base.').replace('{count}', String(entries.length));
      toast.success(questionAnswerImportSummary.value);
    } catch (error) {
      toast.error(t('aiChatAgentSetup.fineTuneQA.qaImportFailed', 'Could not read the Excel file.'));
    }
  };
  reader.readAsArrayBuffer(target.files[0]);
  target.value = '';
}

function relayGeneratedRules(rules) {
  emit('approve-generated-rules', Array.isArray(rules) === true ? rules : []);
}

function relayLearnedQuestionAnswers(entries) {
  emit('learn-question-answers', Array.isArray(entries) === true ? entries : []);
}

onMounted(async () => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 7);
  const endDate = new Date();
  dateRange.value = [startDate, endDate];
  
  if (props.agentID !== '') {
    isInitialReportLoad.value = true;
    await generateReport();
  }
});
const generateReport = async () => {
  if (reportsForbidden.value === true) {
    return;
  }
  if (props.agentID === '') {
    toast.error(t('reports.toasts.agentIdMissing', 'Agent ID is missing.'));
    return;
  }
  if (dateRange.value === null || dateRange.value.length !== 2) {
    toast.error(t('reports.toasts.selectValidDateRange', 'Please select a valid date range.'));
    return;
  }
  
  try {
    const formatDate = (date) => {
      if (date === undefined || date === null) {
        return undefined;
      }
      if (date === dateRange.value[0]) {
        date.setHours(0, 0, 0, 0);
      } else if (date === dateRange.value[1]) {
        date.setHours(23, 59, 59, 999);
      }
      return date.toISOString().slice(0, 19).replace('T', ' ');
    };

    const response = await axios.post(`${globalState.apiEndpoint}/api/reports/generate`, {
      agentIds: [props.agentID],
      reportType: selectedReport.value,
      startDate: formatDate(dateRange.value[0]),
      endDate: formatDate(dateRange.value[1])
    }, { headers: getAuthHeaders() });

    if (response.data !== undefined && response.data !== null && Array.isArray(response.data) === true) {
      reportData.value = response.data;
    } else if (response.data !== undefined && response.data !== null && Array.isArray(response.data.data) === true) {
      reportData.value = response.data.data;
    } else {
      reportData.value = [];
      console.error('Unexpected report data format:', response.data);
    }
    
    toast.dismiss();
    if (isInitialReportLoad.value === false) {
      toast.success(t('reports.toasts.reportGeneratedSuccess', 'Report generated successfully'));
    } else {
      isInitialReportLoad.value = false;
    }
  } catch (error) {
    const status = error?.response?.status;
    if (status === 403) {
      reportsForbidden.value = true;
      reportsForbiddenMessage.value = t('reports.forbiddenMessage', 'You do not have permission to view this report.');
      toast.dismiss();
      reportData.value = [];
      return;
    }
    toast.dismiss();
    toast.error(t('reports.toasts.failedToGenerateReport', 'Failed to generate report'));
    console.error('Error generating report:', error);
    handleAuthError(error);
    reportData.value = [];
  }
};
const formatDisplayDate = (value) => {
  if ((value === undefined) || (value === null)) {
    return '';
  }
  const stringValue = String(value);
  if (stringValue.trim().length === 0) {
    return '';
  }
  const date = new Date(stringValue);
  if (Number.isNaN(date.getTime()) === true) {
    return stringValue;
  }
  let locale = undefined;
  if (typeof currentLanguage.value === 'string' && currentLanguage.value.trim().length > 0) {
    if (currentLanguage.value === 'he') {
      locale = 'he-IL';
    } else if (currentLanguage.value === 'ar') {
      locale = 'ar';
    } else {
      locale = currentLanguage.value;
    }
  }
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  } catch (error) {
    return date.toLocaleString();
  }
};
const formatSessionId = (value) => {
  if ((value === undefined) || (value === null)) {
    return '';
  }
  const trimmedValue = String(value).trim();
  if (trimmedValue.length <= 12) {
    return trimmedValue;
  }
  const prefix = trimmedValue.slice(0, 6);
  const suffix = trimmedValue.slice(trimmedValue.length - 4);
  return `${prefix}...${suffix}`;
};
const showAddAnswerModal = ref(false);
const newAnswer = ref('');
const newAnswerTextarea = ref(null);
const autoResizeNewAnswer = () => {
  const textarea = newAnswerTextarea.value;
  if (textarea === null) {
    return;
  }
  textarea.style.height = 'auto';
  const computedLineHeight = window.getComputedStyle(textarea).lineHeight;
  const lineHeight = (computedLineHeight !== undefined && computedLineHeight !== null && computedLineHeight !== '') ? parseInt(computedLineHeight, 10) : 24;
  const minRows = 10;
  const minHeight = lineHeight * minRows;
  const maxHeight = 1400;
  const newHeight = Math.max(textarea.scrollHeight, minHeight);
  textarea.style.height = `${newHeight <= maxHeight ? newHeight : maxHeight}px`;
}
const bulkAnswerRefs = ref([]);
const autoGrowIfNeeded = (el) => {
  if (el === null) {
    return;
  }
  el.style.height = 'auto';
  const computedLineHeight = window.getComputedStyle(el).lineHeight;
  const lineHeight = (computedLineHeight !== undefined && computedLineHeight !== null && computedLineHeight !== '') ? parseInt(computedLineHeight, 10) : 24;
  const minRows = 4;
  const minHeight = lineHeight * minRows;
  const maxHeight = 800;
  const newHeight = Math.max(el.scrollHeight, minHeight);
  el.style.height = `${newHeight <= maxHeight ? newHeight : maxHeight}px`;
}
const scrollLocked = ref(false);
const previousScrollY = ref(0);
const lockScroll = () => {
  if (scrollLocked.value === true) {
    return;
  }
  if ((typeof window !== 'undefined') && (typeof document !== 'undefined') && (document.body !== undefined) && (document.documentElement !== undefined)) {
    previousScrollY.value = (window.scrollY !== undefined) ? window.scrollY : (window.pageYOffset !== undefined) ? window.pageYOffset : 0;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${previousScrollY.value}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.height = '100vh';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }
  scrollLocked.value = true;
}
const unlockScroll = () => {
  if (scrollLocked.value === false) {
    return;
  }
  if ((typeof window !== 'undefined') && (typeof document !== 'undefined') && (document.body !== undefined) && (document.documentElement !== undefined)) {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    window.scrollTo(0, previousScrollY.value);
  }
  scrollLocked.value = false;
}
const closeAddAnswer = () => {
  showAddAnswerModal.value = false;
  unlockScroll();
}
const closeManualAdd = () => {
  showManualAddModal.value = false;
  unlockScroll();
}
const closeBulkAdd = () => {
  showBulkAddAnswerModal.value = false;
  unlockScroll();
}
const currentRow = ref(null);
const openAnswerModal = (item) => {
  console.log('Opening fine-tune modal for item:', JSON.stringify(item));
  currentRow.value = item;
  newAnswer.value = item.response;
  showAddAnswerModal.value = true;
  nextTick(() => {
    autoResizeNewAnswer();
    lockScroll();
  });
};
onMounted(() => {
  nextTick(() => {
    if (newAnswerTextarea.value !== null) {
      autoResizeNewAnswer();
    }
  });
});
onBeforeUnmount(() => {
  unlockScroll();
});
const currentOriginalAnswer = computed(() => {
  if ((currentRow.value !== null) && (currentRow.value.response !== undefined)) {
    return currentRow.value.response;
  }
  return '';
});
const saveAnswer = () => {
  if (currentRow.value !== null && 
      currentRow.value.userquery !== undefined && 
      currentRow.value.response !== undefined && 
      newAnswer.value.trim().length > 0) {
    
    console.log('Inside saveAnswer, before emit. currentRow:', JSON.stringify(currentRow.value));
    emit('save-fine-tune', {
      originalQuery: currentRow.value.userquery,
      originalResponse: currentRow.value.response, 
      fineTunedAnswer: newAnswer.value.trim()
    });
    toast.dismiss();
    toast.success(t('reports.toasts.fineTuneRequestSubmitted', 'Fine-tune request submitted'));
    newAnswer.value = '';
    currentRow.value = null;
    showAddAnswerModal.value = false;
    unlockScroll();
  } else {
    if (newAnswer.value.trim().length === 0) {
        toast.dismiss();
        toast.error(t('reports.toasts.enterFineTunedAnswer', 'Please enter a fine-tuned answer.'));
    } else {
        console.error("Error saving fine-tune: currentRow is invalid or missing properties.", currentRow.value);
        toast.dismiss();
        toast.error(t('reports.toasts.couldNotSaveFineTune', 'Could not save fine-tuned answer. Invalid data.'));
        newAnswer.value = '';
        currentRow.value = null;
        showAddAnswerModal.value = false;
        unlockScroll();
    }
  }
};
const exportToXLSX = () => {
  if (reportData.value.length === 0) {
    toast.warning(t('reports.toasts.noDataToExport', 'No data to export.'));
    return;
  }
  const columns = getExportColumnLabels();
  const dataToExport = reportData.value.map(item => {
    const row = {};
    row[columns.userQuery] = item.userquery;
    row[columns.aiResponse] = item.response;
    row[columns.timestamp] = formatDisplayDate(item.createdat);
    row[columns.feedback] = item.feedbacktype;
    row[columns.fineTunedAnswer] = '';
    return row;
  });

  const worksheet = XLSX.utils.json_to_sheet(dataToExport);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, t('reports.fineTune.exportSheetName', 'Fine-Tune Data'));
  XLSX.writeFile(workbook, t('reports.fineTune.exportFileName', 'FineTuneReport.xlsx'));
  toast.success(t('reports.toasts.reportExportedSuccess', 'Report exported successfully.'));
};

const handleImport = (event) => {
  const file = event.target.files[0];
  if ((file === undefined) || (file === null)) {
    return;
  }
  const reader = new FileReader();
  reader.onload = (e) => {
    const data = new Uint8Array(e.target.result);
    const workbook = XLSX.read(data, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet);

    const columns = getExportColumnLabels();
    let importedCount = 0;
    for (let rowIndex = 0; rowIndex < jsonData.length; rowIndex = rowIndex + 1) {
      const row = jsonData[rowIndex];
      const userQuery = readImportCell(row, columns.userQuery, 'User Query');
      const fineTunedAnswer = readImportCell(row, columns.fineTunedAnswer, 'Fine-Tuned Answer');
      const aiResponse = readImportCell(row, columns.aiResponse, 'AI Response');

      if (userQuery !== undefined && userQuery !== null && String(userQuery).trim() !== '' && fineTunedAnswer !== undefined && fineTunedAnswer !== null && String(fineTunedAnswer).trim() !== '') {
        emit('save-fine-tune', {
          originalQuery: String(userQuery).trim(),
          originalResponse: aiResponse !== undefined && aiResponse !== null ? aiResponse : '',
          fineTunedAnswer: String(fineTunedAnswer).trim(),
          suppressToast: true
        });
        importedCount = importedCount + 1;
      }
    }

    if (importedCount > 0) {
      toast.success(t('reports.toasts.importedPairsCount', '{count} Q&A pairs imported for fine-tuning.').replace('{count}', String(importedCount)));
    } else {
      toast.warning(t('reports.toasts.noValidImportRows', 'No valid Q&A pairs found in the imported file. Make sure the required columns are present and filled.'));
    }
  };
  reader.readAsArrayBuffer(file);
  event.target.value = '';
};

const saveManualQA = () => {
  if (manualQuestion.value.trim().length > 0 && manualAnswer.value.trim().length > 0) {
    emit('save-fine-tune', {
      originalQuery: manualQuestion.value.trim(),
      originalResponse: '',
      fineTunedAnswer: manualAnswer.value.trim()
    });
    toast.success(t('reports.toasts.newQASubmitted', 'New Q&A submitted for fine-tuning.'));
    manualQuestion.value = '';
    manualAnswer.value = '';
    showManualAddModal.value = false;
  } else {
    toast.error(t('reports.toasts.bothQuestionAnswerRequired', 'Both question and answer are required.'));
  }
};

const itemsSelected = ref([]);
const showBulkAddAnswerModal = ref(false);
const bulkAnswers = ref([]);
const isReasoningModalOpen = ref(false);
const reasoningSessionId = ref('');
const reasoningMessageId = ref('');
const isSourcesModalOpen = ref(false);
const sourcesSessionId = ref('');
const sourcesMessageId = ref('');

const openBulkAnswerModal = () => {
  if (itemsSelected.value.length === 0) {
    toast.warning(t('reports.toasts.selectAtLeastOneRow', 'Please select at least one row to fine-tune.'));
    return;
  }
  bulkAnswers.value = Array(itemsSelected.value.length).fill('');
  showBulkAddAnswerModal.value = true;
  lockScroll();
};

const saveBulkAnswers = () => {
  let savedCount = 0;
  for (let index = 0; index < itemsSelected.value.length; index = index + 1) {
    const item = itemsSelected.value[index];
    const newAnswer = bulkAnswers.value[index];
    if ((newAnswer !== undefined) && (newAnswer !== null) && (newAnswer.trim().length > 0)) {
      emit('save-fine-tune', {
        originalQuery: item.userquery,
        originalResponse: item.response,
        fineTunedAnswer: newAnswer.trim(),
        suppressToast: true
      });
      savedCount = savedCount + 1;
    }
  }

  if (savedCount > 0) {
    toast.success(t('reports.toasts.bulkAnswersSubmittedCount', '{count} answers submitted for fine-tuning.').replace('{count}', String(savedCount)));
  }

  showBulkAddAnswerModal.value = false;
  unlockScroll();
  itemsSelected.value = [];
  bulkAnswers.value = [];
};

const openReasoningModal = (record) => {
  const sessionId = typeof record?.sessionid === 'string' ? record.sessionid.trim() : '';
  if (sessionId.length === 0) {
    toast.error(t('reports.reasoningModal.invalidSession'));
    return;
  }
  const messageId = typeof record?.messageid === 'string' ? record.messageid.trim() : '';
  if (messageId.length === 0) {
    toast.error(t('reports.sourcesModal.invalidMessage'));
    return;
  }
  reasoningSessionId.value = sessionId;
  reasoningMessageId.value = messageId;
  isReasoningModalOpen.value = true;
};

const closeReasoningModal = () => {
  isReasoningModalOpen.value = false;
  reasoningSessionId.value = '';
  reasoningMessageId.value = '';
};

const openSourcesModal = (sessionId, messageId) => {
  if (typeof sessionId !== 'string') {
    toast.error(t('reports.sourcesModal.invalidSession'));
    return;
  }
  const trimmedSession = sessionId.trim();
  if (trimmedSession.length === 0) {
    toast.error(t('reports.sourcesModal.invalidSession'));
    return;
  }
  const trimmedMessageId = typeof messageId === 'string' ? messageId.trim() : '';
  if (trimmedMessageId.length === 0) {
    toast.error(t('reports.sourcesModal.invalidMessage'));
    return;
  }
  sourcesSessionId.value = trimmedSession;
  sourcesMessageId.value = trimmedMessageId;
  isSourcesModalOpen.value = true;
};

const closeSourcesModal = () => {
  isSourcesModalOpen.value = false;
  sourcesSessionId.value = '';
  sourcesMessageId.value = '';
};

watch(() => (showAddAnswerModal.value === true) || (showManualAddModal.value === true) || (showBulkAddAnswerModal.value === true), (isOpen) => {
  if (isOpen === true) {
    lockScroll();
  } else {
    unlockScroll();
  }
});

const filteredReportData = computed(() => {
  if (selectedFeedbackFilter.value === 'all') {
    return reportData.value;
  }
  return reportData.value.filter(item => item.feedbacktype === selectedFeedbackFilter.value);
});
</script>

<style scoped>
table {
  font-family: "Heebo", sans-serif !important;
}
.vue3-easy-data-table {
  height: 95%;
}
.fine-tune-table-shell--rtl td {
  direction: rtl !important;
}
.fine-tune-table-shell--ltr td {
  direction: ltr !important;
}
::deep(.vue3-easy-data-table__header th:first-child),
::deep(.vue3-easy-data-table__body td:first-child) {
  width: 48px;
  min-width: 48px;
  max-width: 48px;
}
div.form-control {
  width: -webkit-fill-available;
  margin-left: 2%;
  margin-right: 2%;
}
.ai-response {
  max-width: 400px;
  overflow-wrap: break-word;
  white-space: pre-wrap;
}
.ai-response--rtl {
  direction: rtl;
}
.ai-response--ltr {
  direction: ltr;
}
.user-query-cell {
  max-width: 200px;
  white-space: normal;
  overflow-wrap: anywhere;
}
.fine-tune-table-shell--rtl .user-query-cell {
  direction: rtl;
}
.fine-tune-table-shell--ltr .user-query-cell {
  direction: ltr;
}
.session-id-cell {
  max-width: 88px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  direction: ltr;
}
.timestamp-cell {
  max-width: 160px;
  white-space: normal;
  overflow-wrap: anywhere;
  direction: ltr;
}
.fine-tune-actions-row {
  margin-left: 1.75rem;
  margin-right: 1.75rem;
}
.fine-tune-actions-row > * + * {
  margin-left: 0.5rem;
}
.fine-tune-actions-row--rtl > * + * {
  margin-left: 0;
  margin-right: 0.5rem;
}
.fine-tune-import-label {
  margin-left: 1rem;
  margin-right: 1rem;
}
.fine-tune-actions-row--rtl .fine-tune-import-label {
  margin-left: 1rem;
  margin-right: 1rem;
}
.fine-tune-action-icon {
  margin-right: 0.5rem;
}
.fine-tune-actions-row--rtl .fine-tune-action-icon {
  margin-right: 0;
  margin-left: 0.5rem;
}
.fine-tune-filters-row > * + * {
  margin-left: 1rem;
}
.fine-tune-table-shell--rtl .fine-tune-filters-row > * + * {
  margin-left: 0;
  margin-right: 1rem;
}
.fine-tune-table-shell {
  margin-left: 0.75rem;
}
.fine-tune-row-actions > * + * {
  margin-left: 0.5rem;
}
.fine-tune-row-actions--rtl > * + * {
  margin-left: 0;
  margin-right: 0.5rem;
}
.fine-tune-row-action-icon {
  margin-right: 0.25rem;
}
.fine-tune-row-actions--rtl .fine-tune-row-action-icon {
  margin-right: 0;
  margin-left: 0.25rem;
}
.original-answer-card {
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  color: #374151;
  padding: 0.75rem;
  border-radius: 0.5rem;
  max-height: 50vh;
  overflow: auto;
  white-space: pre-wrap;
}
.fine-tune-table-shell--rtl .original-answer-card {
  direction: rtl;
  text-align: right;
}
.fine-tune-table-shell--ltr .original-answer-card {
  direction: ltr;
  text-align: left;
}
.modal {
  position: fixed;
  inset: 0;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-box {
  background-color: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 500px;
}
.modal-box-md {
  max-width: 640px;
}
.modal-box-lg {
  max-width: 900px;
}
.modal-box h3 {
  margin-bottom: 0.5rem;
}
.modal-header {
  border-bottom: 1px solid rgba(0,0,0,0.06);
  padding-bottom: 0.5rem;
}
.modal-body {
  padding-top: 1rem;
}
.modal-box .label-text {
  font-weight: 500;
}
.modal-box .textarea {
  min-height: 48px;
  border-radius: 0.5rem;
}
.new-answer-textarea {
  min-height: 360px;
}
.auto-grow-textarea {
  overflow: hidden;
  transition: height 0.15s ease;
}
.custom-modal {
  position: fixed;
  inset: 0;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}
.custom-modal-box {
  background-color: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  width: 95%;
  max-width: 1100px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.custom-modal-box-xl {
  max-width: 1200px;
}
.custom-modal-header {
  border-bottom: 1px solid rgba(0,0,0,0.06);
  padding-bottom: 0.5rem;
}
.custom-modal-body {
  padding-top: 1rem;
  overflow: auto;
}
.custom-modal-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0,0,0,0.06);
}
.custom-modal-footer > * + * { margin-left: 0.5rem; }
.modal-action {
  display: flex;
  justify-content: flex-end;
}
.modal-action > * + * { margin-left: 0.5rem; }
</style>
